import { Settings, SwitchApplication } from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import React from "react";
import { useSelector } from "react-redux";

import { AssetIcon } from "components/icons/AssetIcon";
import { Connections } from "components/icons/Connections";
import { Dashboard } from "components/icons/Dashboard";
import { FileSharing } from "components/icons/FileSharing";
import { Home } from "components/icons/Home";
import { IntegrationsIcon } from "components/icons/IntegrationsIcon";
import { Learn } from "components/icons/Learn";
import { Licenses } from "components/icons/Licenses";
import { Reports } from "components/icons/Reports";
import { Users } from "components/icons/Users";
import { Workflows } from "components/icons/Workflows";
import {
    ASSET_MANAGEMENT_ROUTE,
    ASSET_MANAGEMENT_ROUTE_BMP,
    BAM_FILE_SHARING_ROUTE,
    BAM_LICENSES_ROUTE,
    BAM_SUPPORT_ROUTE,
    BAM_USERS_ROUTE,
    BAM_WORKFLOWS_ROUTE,
    CONNECTIONS_ROUTE,
    DASHBOARD_ROUTE,
    HOME_ROUTE,
    INTEGRATIONS_ROUTE,
    LICENSES_ROUTE,
    PRODUCTS_ROUTE,
    REPORTS_ROUTE,
    RouteDefinition,
    SETTINGS_ROUTE,
    SUPPORT_ROUTE,
    USERS_ROUTE,
    WORKFLOWS_ROUTE,
} from "components/router/Routes";
import { FLAG_ASSET_MANAGEMENT } from "services/feature/FeatureFlagService";
import { StoreState } from "store";
import { Application } from "store/application";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

export interface MainMenuItem {
    title: string;
    mainRoute: RouteDefinition;
    icon: JSX.Element;
    testId: string;
}

export function useMainMenuItems() {
    const application = useSelector((state: StoreState) => state.applicationReducer.selected);
    const assetManagementEnabled = useFeature(FLAG_ASSET_MANAGEMENT);

    const currentPath = window.location.pathname + window.location.search;

    const hasActiveLink = (routes: RouteDefinition[]): boolean => {
        const isRouteOrChildActive = (route: RouteDefinition): boolean => {
            if (route.path === currentPath) {
                return true;
            }

            return route.childRoutes.some((childRoute) => isRouteOrChildActive(childRoute));
        };

        return routes.some((route) => isRouteOrChildActive(route));
    };

    const getIconColor = (route: RouteDefinition) => {
        const isRouteActive = currentPath === route.path;
        const isChildActive = hasActiveLink(route.childRoutes);

        return isRouteActive || isChildActive
            ? resolveColor("--app-mainNavigationPanel-iconButton-iconColor-activeColor")
            : resolveColor("--app-mainNavigationPanel-iconButton-iconColor");
    };

    const bmpMainMenuItems: MainMenuItem[] = [
        {
            title: HOME_ROUTE.title,
            mainRoute: HOME_ROUTE,
            icon: <Home color={getIconColor(HOME_ROUTE)} />,
            testId: testIds.navigation.home.itself,
        },
        {
            title: DASHBOARD_ROUTE.title,
            mainRoute: DASHBOARD_ROUTE,
            icon: <Dashboard color={getIconColor(DASHBOARD_ROUTE)} />,
            testId: testIds.navigation.dashboard.itself,
        },
        {
            title: PRODUCTS_ROUTE.title,
            mainRoute: PRODUCTS_ROUTE,
            icon: <SwitchApplication size={24} color={getIconColor(PRODUCTS_ROUTE)} />,
            testId: testIds.navigation.products.itself,
        },
        {
            title: CONNECTIONS_ROUTE.title,
            mainRoute: CONNECTIONS_ROUTE,
            icon: <Connections size={24} color={getIconColor(CONNECTIONS_ROUTE)} />,
            testId: testIds.navigation.connections.itself,
        },
        {
            title: REPORTS_ROUTE.title,
            mainRoute: REPORTS_ROUTE,
            icon: <Reports color={getIconColor(REPORTS_ROUTE)} />,
            testId: testIds.navigation.reports.itself,
        },
        {
            title: WORKFLOWS_ROUTE.title,
            mainRoute: WORKFLOWS_ROUTE,
            icon: <Workflows color={getIconColor(WORKFLOWS_ROUTE)} />,
            testId: testIds.navigation.workflows.itself,
        },
        {
            title: SETTINGS_ROUTE.title,
            mainRoute: SETTINGS_ROUTE,
            icon: <Settings color={getIconColor(SETTINGS_ROUTE)} />,
            testId: testIds.navigation.settings.itself,
        },
    ];

    const assetOverviewMenuItem: MainMenuItem = {
        title: ASSET_MANAGEMENT_ROUTE_BMP.title,
        mainRoute: ASSET_MANAGEMENT_ROUTE_BMP,
        icon: <AssetIcon color={getIconColor(ASSET_MANAGEMENT_ROUTE_BMP)} />,
        testId: testIds.navigation.assets.itself,
    };

    const bmpWithAssetsMenuItems: MainMenuItem[] = [...bmpMainMenuItems, assetOverviewMenuItem];

    const bamMainMenuItems: MainMenuItem[] = [
        {
            title: ASSET_MANAGEMENT_ROUTE.title,
            mainRoute: ASSET_MANAGEMENT_ROUTE,
            icon: <AssetIcon color={getIconColor(ASSET_MANAGEMENT_ROUTE)} />,
            testId: testIds.navigation.assets.itself,
        },
        {
            title: BAM_LICENSES_ROUTE.title,
            mainRoute: BAM_LICENSES_ROUTE,
            icon: <Licenses color={getIconColor(LICENSES_ROUTE)} />,
            testId: testIds.navigation.licenses.itself,
        },
        {
            title: BAM_USERS_ROUTE.title,
            mainRoute: BAM_USERS_ROUTE,
            icon: <Users color={getIconColor(USERS_ROUTE)} />,
            testId: testIds.navigation.users.itself,
        },
        {
            title: INTEGRATIONS_ROUTE.title,
            mainRoute: INTEGRATIONS_ROUTE,
            icon: <IntegrationsIcon color={getIconColor(INTEGRATIONS_ROUTE)} />,
            testId: testIds.navigation.integrations.itself,
        },
        {
            title: BAM_WORKFLOWS_ROUTE.title,
            mainRoute: BAM_WORKFLOWS_ROUTE,
            icon: <Workflows color={getIconColor(BAM_WORKFLOWS_ROUTE)} />,
            testId: testIds.navigation.workflows.itself,
        },
        {
            title: BAM_FILE_SHARING_ROUTE.title,
            mainRoute: BAM_FILE_SHARING_ROUTE,
            icon: <FileSharing color={getIconColor(BAM_FILE_SHARING_ROUTE)} />,
            testId: testIds.navigation.fileSharing.itself,
        },
        {
            title: BAM_SUPPORT_ROUTE.title,
            mainRoute: BAM_SUPPORT_ROUTE,
            icon: <Learn color={getIconColor(SUPPORT_ROUTE)} />,
            testId: testIds.navigation.support.itself,
        },
    ];

    const bmpSupportMenuItem: MainMenuItem = {
        title: SUPPORT_ROUTE.title,
        mainRoute: SUPPORT_ROUTE,
        icon: <Learn color={getIconColor(SUPPORT_ROUTE)} />,
        testId: testIds.navigation.support.itself,
    };

    const items =
        application === Application.BAM
            ? bamMainMenuItems
            : assetManagementEnabled
              ? bmpWithAssetsMenuItems
              : bmpMainMenuItems;

    const isSupportRouteActive =
        bmpSupportMenuItem.mainRoute.path === currentPath || hasActiveLink(bmpSupportMenuItem.mainRoute.childRoutes);

    const activeMainMenuItem =
        items.find((item) => item.mainRoute.path === currentPath || hasActiveLink(item.mainRoute.childRoutes)) ??
        (isSupportRouteActive ? bmpSupportMenuItem : undefined);

    return { items, hasActiveLink, getIconColor, currentPath, activeMainMenuItem, bmpSupportMenuItem };
}
